import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowsClockwise, DownloadSimple, Warning, Clock, CheckCircle, Certificate, Paperclip, X as XIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  valida:      { label: "Valida",      cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
  in_scadenza: { label: "In scadenza", cls: "bg-amber-100 text-amber-800 border-amber-200",       icon: Clock },
  scaduta:     { label: "Scaduta",     cls: "bg-red-100 text-red-800 border-red-200",             icon: Warning },
  permanente:  { label: "Permanente",  cls: "bg-blue-100 text-blue-800 border-blue-200",          icon: Certificate },
  sconosciuto: { label: "—",           cls: "bg-gray-100 text-gray-700 border-gray-200",          icon: Certificate },
};

export default function EmployeeCertificationsTab({ employees }) {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState({ items: [], expired: 0, expiring_30: 0 });
  const [days, setDays] = useState("all"); // all|7|14|30|60|90
  const [empFilter, setEmpFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const buildParams = useCallback(() => {
    const p = {};
    if (days !== "all") p.days = parseInt(days, 10);
    if (empFilter && empFilter !== "all") p.employee_id = empFilter;
    if (statusFilter && statusFilter !== "all") p.status = statusFilter;
    return p;
  }, [days, empFilter, statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/certifications-aggregated`, {
        params: buildParams(), withCredentials: true,
      });
      setData(r.data || { items: [], expired: 0, expiring_30: 0 });
    } catch (e) {
      if (e.response?.status === 401) toast.error("Sessione scaduta");
      else toast.error("Errore caricamento certificazioni");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Re-load on filter change OR workspace switch (Batch 62.A pattern)
  useEffect(() => { load(); }, [load, activeWorkspaceId]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/certifications-aggregated/csv`, {
        params: buildParams(), withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificazioni_workspace${days !== "all" ? `_${days}d` : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch {
      toast.error("Errore export CSV");
    }
  };

  const downloadDoc = async (cid, filename) => {
    try {
      const r = await axios.get(`${API}/certifications-v2/${cid}/document`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "certificazione";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download documento");
    }
  };

  const employeesActive = (employees || []).filter(e => !e.deleted_at);
  const formatDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return s; }
  };
  const resetFilters = () => { setDays("all"); setEmpFilter("all"); setStatusFilter("all"); };

  return (
    <div className="space-y-4" data-testid="employee-certifications-tab">
      {/* Counter + filters */}
      <Card className="p-4 border-[#0D1F3C]/8">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Badge className="bg-red-100 text-red-800 border-red-200" data-testid="cert-counter-expired">
              <Warning size={12} weight="fill" className="mr-1" /> {data.expired} scadute
            </Badge>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="cert-counter-expiring">
              <Clock size={12} weight="fill" className="mr-1" /> {data.expiring_30} in scadenza ≤30gg
            </Badge>
          </div>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <Select value={empFilter} onValueChange={setEmpFilter}>
              <SelectTrigger className="w-[180px] h-9" data-testid="cert-emp-filter">
                <SelectValue placeholder="Tutti i dipendenti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i dipendenti</SelectItem>
                {employeesActive.map(e => (
                  <SelectItem key={e.id} value={e.id}>
                    {`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[140px] h-9" data-testid="cert-days-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="7">≤ 7 giorni</SelectItem>
                <SelectItem value="14">≤ 14 giorni</SelectItem>
                <SelectItem value="30">≤ 30 giorni</SelectItem>
                <SelectItem value="60">≤ 60 giorni</SelectItem>
                <SelectItem value="90">≤ 90 giorni</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-9" data-testid="cert-status-filter">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="scaduta">Scadute</SelectItem>
                <SelectItem value="in_scadenza">In scadenza</SelectItem>
                <SelectItem value="valida">Valide</SelectItem>
                <SelectItem value="permanente">Permanenti</SelectItem>
              </SelectContent>
            </Select>

            {(days !== "all" || empFilter !== "all" || statusFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-[#0D1F3C]/60 gap-1" data-testid="cert-reset-filters">
                <XIcon size={14} /> Reset
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={load} className="gap-1" data-testid="cert-refresh-btn">
              <ArrowsClockwise size={14} /> Aggiorna
            </Button>
            <Button size="sm" onClick={exportCsv} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] gap-1" data-testid="cert-export-csv-btn">
              <DownloadSimple size={14} /> Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-[#0D1F3C]/8">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#0D1F3C]/50" data-testid="cert-loading">Caricamento…</div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center" data-testid="cert-empty-state">
            <Certificate size={32} weight="duotone" className="mx-auto text-[#0D1F3C]/20 mb-3" />
            <p className="text-sm font-medium text-[#0D1F3C]">Nessuna certificazione trovata{days !== "all" ? ` con scadenza ≤ ${days} giorni` : ""}</p>
            <p className="text-xs text-[#0D1F3C]/50 mt-1">Aggiungi certificazioni dalla scheda dipendente.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Dipendente</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Certificazione</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Ente</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Scadenza</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Giorni</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Stato</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right">Doc.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map(it => {
                const meta = STATUS_META[it.status_calc] || STATUS_META.sconosciuto;
                const Icon = meta.icon;
                const negDays = it.days_to_expiry !== null && it.days_to_expiry < 0;
                const dayCellCls = negDays
                  ? "text-red-600 font-semibold"
                  : (it.days_to_expiry !== null && it.days_to_expiry <= 7 ? "text-amber-700 font-semibold" : "text-[#0D1F3C]/60");
                return (
                  <TableRow key={it.id} data-testid={`cert-row-${it.id}`}>
                    <TableCell className="text-sm font-medium">
                      <Link to={`/employees?tab=schede&expand=${it.employee_id}`} className="text-[#1B4FD8] hover:underline">
                        {it.employee_name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm text-[#0D1F3C]/80">{it.name}</TableCell>
                    <TableCell className="text-xs text-[#0D1F3C]/50">{it.issuing_body || "—"}</TableCell>
                    <TableCell className="text-xs font-mono text-[#0D1F3C]/60">
                      {it.no_expiry ? "Permanente" : formatDate(it.expiry_date)}
                    </TableCell>
                    <TableCell className={`text-xs font-mono ${dayCellCls}`}>
                      {it.days_to_expiry !== null
                        ? (it.days_to_expiry < 0 ? `-${Math.abs(it.days_to_expiry)}` : it.days_to_expiry)
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${meta.cls} text-xs gap-1 border`}>
                        <Icon size={11} weight="fill" /> {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {it.document_file_id ? (
                        <button
                          onClick={() => downloadDoc(it.id, it.document_filename)}
                          title={`Scarica ${it.document_filename}`}
                          className="text-[#1B4FD8] hover:opacity-70 inline-flex items-center"
                          data-testid={`cert-doc-${it.id}`}
                        >
                          <Paperclip size={14} weight="duotone" />
                        </button>
                      ) : <span className="text-[#0D1F3C]/30 text-xs">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
